import { useEffect, useState, useCallback } from 'react';
import { SuperAdminSidebar } from '../../components/Sidebar';
import QuestionModal from '../../components/QuestionModal';
import api from '../../utils/api';

const LEVEL_INFO = {
  easy:   { cls: 'badge-green',  label: 'Leicht' },
  medium: { cls: 'badge-orange', label: 'Mittel' },
  hard:   { cls: 'badge-red',    label: 'Schwer' },
};

export default function SuperAdminQuestions() {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', level: '', type: '', search: '' });
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get('/superadmin/questions/categories').then(r => setCategories(r.data.categories));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: 200 });
    if (filter.category) p.set('category', filter.category);
    if (filter.level) p.set('level', filter.level);
    if (filter.type) p.set('type', filter.type);
    if (filter.search) p.set('search', filter.search);
    api.get(`/questions?${p}`)
      .then(r => { setQuestions(r.data.questions); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (q, isEdit) => {
    if (isEdit) {
      setQuestions(prev => prev.map(x => x.id === q.id ? { ...x, ...q } : x));
    } else {
      setQuestions(prev => [{ ...q, is_central: true }, ...prev]);
      setTotal(t => t + 1);
    }
    setModal(null);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/questions/${id}`);
      setQuestions(prev => prev.filter(q => q.id !== id));
      setTotal(t => t - 1);
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    }
    setDeleteId(null);
  };

  const setF = (key, val) => setFilter(p => ({ ...p, [key]: val }));

  const centralCount = questions.filter(q => !q.mosque_id).length;

  return (
    <div style={{ display: 'flex' }}>
      <SuperAdminSidebar />
      <main className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div className="page-header" style={{ margin: 0 }}>
            <h1>Fragen-Datenbank</h1>
            <p>{total} Fragen gesamt · {centralCount} zentrale Fragen</p>
          </div>
          <button className="btn btn-primary" onClick={() => setModal('new')}>
            + Zentrale Frage erstellen
          </button>
        </div>

        {/* Info box */}
        <div style={{ background: 'var(--primary-lt)', border: '1px solid #c8dfd5', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: 'var(--primary-dk)' }}>
          <strong>Zentrale Fragen</strong> sind für alle Moscheen sichtbar. Fragen mit Moschee-Zuordnung sind nur für diese Moschee sichtbar.
          <br />Alle Fragen müssen in beiden Sprachen (Deutsch + Shqip) eingegeben werden.
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            className="form-input" style={{ width: 260 }}
            placeholder="Suchen (DE oder AL)..."
            value={filter.search}
            onChange={e => setF('search', e.target.value)}
          />
          <select className="form-select" style={{ width: 180 }} value={filter.category} onChange={e => setF('category', e.target.value)}>
            <option value="">Alle Kategorien</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name_de}</option>)}
          </select>
          <select className="form-select" style={{ width: 140 }} value={filter.level} onChange={e => setF('level', e.target.value)}>
            <option value="">Alle Niveaus</option>
            <option value="easy">Leicht</option>
            <option value="medium">Mittel</option>
            <option value="hard">Schwer</option>
          </select>
          <select className="form-select" style={{ width: 160 }} value={filter.type} onChange={e => setF('type', e.target.value)}>
            <option value="">Alle Typen</option>
            <option value="multiple_choice">Multiple Choice</option>
            <option value="true_false">Wahr / Falsch</option>
          </select>
        </div>

        <div className="card">
          {loading ? (
            <div className="spinner-wrap"><div className="spinner" /></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Frage (DE / AL)</th>
                    <th>Kategorie</th>
                    <th>Niveau</th>
                    <th>Typ</th>
                    <th>Quelle</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map(q => {
                    const lvl = LEVEL_INFO[q.level] || { cls: 'badge-gray', label: q.level };
                    return (
                      <tr key={q.id}>
                        <td style={{ maxWidth: 400 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{q.text_de}</div>
                          {q.text_al && (
                            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3, fontStyle: 'italic' }}>
                              {q.text_al.length > 90 ? q.text_al.slice(0, 90) + '…' : q.text_al}
                            </div>
                          )}
                          {q.mosque_id ? (
                            <span className="badge badge-blue" style={{ marginTop: 4, fontSize: 10 }}>Moschee-Frage</span>
                          ) : (
                            <span className="badge badge-green" style={{ marginTop: 4, fontSize: 10 }}>Zentral</span>
                          )}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {q.category_name_de}
                        </td>
                        <td><span className={`badge ${lvl.cls}`}>{lvl.label}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                          {q.type === 'true_false' ? 'W/F' : 'MC'}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {q.source_ref || '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm btn-ghost" onClick={() => setModal(q)}>Bearbeiten</button>
                            <button className="btn btn-sm btn-danger" onClick={() => setDeleteId(q.id)}>Löschen</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {questions.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
                  Keine Fragen gefunden
                </div>
              )}
            </div>
          )}
        </div>

        {modal && (
          <QuestionModal
            question={modal === 'new' ? null : modal}
            onClose={() => setModal(null)}
            onSaved={handleSaved}
            apiBase="superadmin"
          />
        )}

        {deleteId && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 400 }}>
              <h3 className="modal-title">Frage löschen?</h3>
              <p style={{ color: 'var(--text2)', marginBottom: 24 }}>
                Diese Frage wird für alle Moscheen entfernt.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={() => handleDelete(deleteId)}>Löschen</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
